import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Sparkles, ArrowLeft, ArrowRight, Camera, X, Loader2, Check, Phone, ShieldCheck, AlertTriangle } from 'lucide-react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { api, fileUrl } from '@/lib/api';
import { compressImageToFile } from '@/lib/imageCompress';

const STEPS = ['Service', 'Scope & Photos', 'Property & Schedule', 'Contact', 'Estimate'];

export default function EstimateWizardPage() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [cats, setCats] = useState([]);
  const [step, setStep] = useState(0);
  const [loadingAI, setLoadingAI] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState(null);

  const [form, setForm] = useState({
    category_slug: searchParams.get('category') || '',
    sub_service_slug: searchParams.get('sub') || '',
    measurements: '',
    description: '',
    photo_paths: [], // [{path, url}]
    uploading: false,
    property_type: 'Residential',
    address: '',
    city: '',
    zip: '',
    occupied: 'occupied',   // occupied | vacant
    access: 'easy',         // easy | limited | difficult
    preferred_date: '',
    urgency: 'standard',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
  });
  // Transient per-file upload state — separate from form so tapping
  // "next" between uploads doesn't wipe the progress rows.
  const [pending, setPending] = useState([]);   // [{id, name, status, error}]

  useEffect(() => {
    api.get('/services').then(r => setCats(r.data.categories || []));
  }, []);

  const category = useMemo(() => cats.find(c => c.slug === form.category_slug) || null, [cats, form.category_slug]);
  const subService = useMemo(() => category?.sub_services.find(s => s.slug === form.sub_service_slug) || null, [category, form.sub_service_slug]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canNext = useMemo(() => {
    if (step === 0) return !!form.category_slug && !!form.sub_service_slug;
    if (step === 1) return !!form.description.trim();
    if (step === 2) return !!form.property_type && /^\d{5}$/.test(form.zip);
    if (step === 3) return form.customer_name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.customer_email);
    return true;
  }, [step, form]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    // Convert FileList to array + de-dupe by name so users mashing the
    // picker doesn't create duplicate rows.
    const list = Array.from(files).filter((f) => f.type?.startsWith('image/'));
    if (list.length === 0) {
      toast.warning('Only image files are supported');
      return;
    }
    const availableSlots = 8 - form.photo_paths.length;
    if (availableSlots <= 0) {
      toast.warning('Max 8 photos — remove one to add more');
      return;
    }
    const accepted = list.slice(0, availableSlots);
    if (list.length > availableSlots) {
      toast.info(`Only ${availableSlots} more photo${availableSlots === 1 ? '' : 's'} allowed`);
    }
    // Seed pending rows so the UI shows tiles immediately.
    const jobs = accepted.map((f) => ({
      id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      name: f.name,
      status: 'queued',    // queued -> compressing -> uploading -> done | error
      error: null,
    }));
    setPending((prev) => [...prev, ...jobs]);
    set({ uploading: true });

    // Compress + upload each job in parallel with a small concurrency
    // cap so we don't hammer a mobile connection. A slower connection
    // still shows PROGRESS tiles instead of one long spinner.
    const CONCURRENCY = 3;
    let cursor = 0;
    const uploadedPhotos = [];

    const patchJob = (id, patch) => {
      setPending((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
    };

    const worker = async () => {
      while (cursor < jobs.length) {
        const idx = cursor++;
        const job = jobs[idx];
        try {
          // Client-side compress: any file over ~1 MB, or larger than 1600px.
          let toUpload = job.file;
          const isLarge = job.file.size > 1_000_000;
          if (isLarge) {
            patchJob(job.id, { status: 'compressing' });
            try {
              toUpload = await compressImageToFile(job.file, {
                maxDim: 1600,
                quality: 0.85,
                maxBytes: 900_000,
              });
            } catch {
              // If compression fails (e.g., HEIC on older browsers),
              // fall back to the raw file. Backend may still accept it.
              toUpload = job.file;
            }
          }
          patchJob(job.id, { status: 'uploading' });
          const fd = new FormData();
          fd.append('file', toUpload);
          const r = await api.post('/uploads', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
          });
          uploadedPhotos.push({ path: r.data.path, url: r.data.url });
          patchJob(job.id, { status: 'done' });
        } catch (e) {
          const msg =
            e?.response?.status === 413
              ? 'Photo too large after compression'
              : e?.response?.data?.detail || 'Upload failed — try again';
          patchJob(job.id, { status: 'error', error: msg });
        }
      }
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, jobs.length) }, worker),
      );
      if (uploadedPhotos.length > 0) {
        set({ photo_paths: [...form.photo_paths, ...uploadedPhotos] });
      }
      const failed = jobs.length - uploadedPhotos.length;
      if (failed > 0) {
        toast.error(
          `${failed} of ${jobs.length} photo${jobs.length === 1 ? '' : 's'} could not be added — please retry`,
        );
      }
    } finally {
      set({ uploading: false });
      if (fileRef.current) fileRef.current.value = '';
      // Clear the pending list after a brief moment so the user sees
      // green checkmarks/errors before the tiles fold in.
      setTimeout(() => {
        setPending((prev) => prev.filter((j) => j.status === 'error'));
      }, 1200);
    }
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setLoadingAI(true);
    // Immediately reveal step 4 so the user sees a live skeleton instead
    // of a frozen "Calculating..." button.
    setStep(4);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        category_slug: category.slug,
        category_name: category.name,
        sub_service_slug: subService.slug,
        sub_service_name: subService.name,
        property_type: form.property_type,
        address: form.address,
        city: form.city,
        zip: form.zip,
        occupied: form.occupied === 'occupied',
        access: form.access,
        measurements: form.measurements,
        description: form.description,
        urgency: form.urgency,
        preferred_date: form.preferred_date,
        photo_paths: form.photo_paths.map((p) => p.path),
      };
      // The pricing engine analyzes scope (incl. photos) + prices every line
      // item from the Royal Palm Price Book — takes ~15-40s.
      const r = await api.post('/estimates', payload, { timeout: 120000 });
      const est = r.data.customer_estimate;
      if (est) {
        setEstimate(est);
        toast.success('Estimate ready!');
      } else {
        toast.warning('Your details are saved. Our team will follow up with a manual quote.');
      }
    } catch (e) {
      toast.error('Submission failed. Please try again.');
      setStep(3);
    } finally {
      setSubmitting(false);
      setLoadingAI(false);
    }
  };

  const next = async () => {
    if (step === 3) {
      await submit();
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <Layout hideFooter={false}>
      {/* Sticky progress */}
      <div className="sticky top-16 md:top-20 z-30 bg-white/95 backdrop-blur border-b border-[#E6E9EE]">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Step {Math.min(step + 1, STEPS.length)} of {STEPS.length}</div>
            <a href="tel:5618848900" className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-[#17352D]"><Phone className="h-3.5 w-3.5" /> Need help? 561-884-8900</a>
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} className="mt-2 h-1.5" />
          <div className="hidden md:flex mt-2 gap-1 text-[11px] text-slate-500">
            {STEPS.map((s, i) => <div key={i} className={`flex-1 ${i <= step ? 'text-[#17352D] font-semibold' : ''}`}>{s}</div>)}
          </div>
        </div>
      </div>

      <section data-testid="estimate-wizard" className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Quick Estimate</div>
              <h1 className="mt-1 font-headline text-3xl md:text-4xl text-[#17352D]">What kind of work do you need?</h1>
              <p className="text-slate-600 mt-2">Choose a category and the specific service. We’ll calculate a ballpark estimate in seconds.</p>
            </div>
            <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Category</Label>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                {cats.map((c) => (
                  <button key={c.slug} type="button"
                    data-testid={`wizard-category-${c.slug}`}
                    onClick={() => set({ category_slug: c.slug, sub_service_slug: '' })}
                    className={`text-left rounded-xl border p-3 transition-colors-only ${form.category_slug === c.slug ? 'border-[#17352D] bg-[#F2F7F5]' : 'border-[#E6E9EE] bg-white hover:bg-[#F7F8FA]'}`}>
                    <div className="font-semibold text-sm text-[#17352D]">{c.name}</div>
                    <div className="text-[11px] text-slate-500 line-clamp-1">{c.sub_services.length} services</div>
                  </button>
                ))}
              </div>
            </div>
            {category && (
              <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Sub-service</Label>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[360px] overflow-y-auto pr-1">
                  {category.sub_services.map((s) => (
                    <button key={s.slug} type="button"
                      data-testid={`wizard-subservice-${s.slug}`}
                      onClick={() => set({ sub_service_slug: s.slug })}
                      className={`text-left rounded-xl border p-3 transition-colors-only ${form.sub_service_slug === s.slug ? 'border-[#17352D] bg-[#F2F7F5]' : 'border-[#E6E9EE] bg-white hover:bg-[#F7F8FA]'}`}>
                      <div className="font-medium text-sm text-[#17352D]">{s.name}</div>
                      <div className="text-[11px] text-slate-500">${s.price_low.toLocaleString()}{s.price_high > s.price_low && <> – ${s.price_high.toLocaleString()}</>} / {s.unit}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Scope & Photos</div>
              <h1 className="mt-1 font-headline text-3xl md:text-4xl text-[#17352D]">Tell us about your project</h1>
              <p className="text-slate-600 mt-2">A few details + 1–8 photos help us give you a more accurate ballpark.</p>
            </div>
            <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5 space-y-4">
              <div>
                <Label htmlFor="meas">Measurements / size</Label>
                <Input id="meas" data-testid="wizard-measurements-input" value={form.measurements} onChange={(e) => set({ measurements: e.target.value })} placeholder="e.g. 180 sq ft kitchen, 12 ft cabinet run" className="mt-1 h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="desc">Describe the work *</Label>
                <Textarea id="desc" data-testid="wizard-description-input" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Tell us about the scope, materials, condition, timeline..." rows={5} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Photos ({form.photo_paths.length}/8)</Label>
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {form.photo_paths.map((p, i) => (
                    <div key={p.path} className="relative aspect-square rounded-xl overflow-hidden border border-[#E6E9EE]" data-testid={`wizard-photo-tile-${i}`}>
                      <img src={fileUrl(p.path)} alt={`upload ${i + 1}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      <button type="button" onClick={() => set({ photo_paths: form.photo_paths.filter((_, idx) => idx !== i) })}
                        data-testid={`wizard-photo-remove-${i}`}
                        aria-label={`Remove photo ${i + 1}`}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/95 text-slate-700 flex items-center justify-center shadow">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {/* Per-file pending tiles — show compress/upload/error state so the customer sees each photo landing */}
                  {pending.map((job) => (
                    <div
                      key={job.id}
                      className={`relative aspect-square rounded-xl overflow-hidden border ${job.status === 'error' ? 'border-red-300 bg-red-50' : 'border-[#C7DED4] bg-[#F2F7F5]'} flex flex-col items-center justify-center text-[10px] font-medium`}
                      data-testid={`wizard-photo-pending-${job.status}`}
                    >
                      {job.status === 'compressing' && (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin text-[#17352D]" />
                          <span className="mt-1 text-slate-600">Optimizing…</span>
                        </>
                      )}
                      {job.status === 'uploading' && (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin text-[#C8922F]" />
                          <span className="mt-1 text-slate-600">Uploading…</span>
                        </>
                      )}
                      {job.status === 'queued' && (
                        <span className="text-slate-500">Waiting…</span>
                      )}
                      {job.status === 'done' && (
                        <>
                          <Check className="h-5 w-5 text-emerald-600" />
                          <span className="mt-1 text-emerald-700">Added</span>
                        </>
                      )}
                      {job.status === 'error' && (
                        <>
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                          <span className="mt-1 px-1.5 text-red-600 text-center leading-tight">
                            {job.error || 'Failed'}
                          </span>
                          <button
                            type="button"
                            onClick={() => setPending((prev) => prev.filter((j) => j.id !== job.id))}
                            className="absolute top-1 right-1 h-5 w-5 rounded-full bg-white/95 text-slate-700 flex items-center justify-center shadow"
                            aria-label="Dismiss error"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  {form.photo_paths.length + pending.filter((j) => j.status !== 'error').length < 8 && (
                    <button type="button" onClick={() => fileRef.current?.click()}
                      data-testid="wizard-photo-upload-button"
                      className="aspect-square rounded-xl border border-dashed border-[#C7DED4] bg-[#F2F7F5] hover:bg-[#E3EFEA] flex flex-col items-center justify-center text-slate-600 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8922F]">
                      {form.uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
                      <span className="mt-1">{form.uploading ? 'Adding…' : 'Add photo'}</span>
                    </button>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                  Snap a photo or upload from your library — we&apos;ll auto-shrink large photos so they upload fast on any connection.
                </p>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="estimate-wizard-photo-upload-input" onChange={(e) => handleUpload(e.target.files)} />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Property & Schedule</div>
              <h1 className="mt-1 font-headline text-3xl md:text-4xl text-[#17352D]">Where and when?</h1>
            </div>
            <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5 space-y-4">
              <div>
                <Label>Property type</Label>
                <RadioGroup value={form.property_type} onValueChange={(v) => set({ property_type: v })} className="mt-2 grid grid-cols-2 gap-2">
                  {['Residential', 'Commercial'].map(t => (
                    <label key={t} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${form.property_type === t ? 'border-[#17352D] bg-[#F2F7F5]' : 'border-[#E6E9EE]'}`}>
                      <RadioGroupItem value={t} id={`pt-${t}`} data-testid={`wizard-prop-${t.toLowerCase()}`} />
                      <span className="text-sm font-medium text-[#17352D]">{t}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label htmlFor="addr">Street address</Label>
                  <Input id="addr" value={form.address} onChange={(e) => set({ address: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="wizard-address-input" />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP code *</Label>
                  <Input id="zip" inputMode="numeric" maxLength={5} value={form.zip}
                    onChange={(e) => set({ zip: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                    placeholder="33410" className="mt-1 h-11 rounded-xl" data-testid="wizard-zip-input" />
                  <p className="text-[11px] text-slate-500 mt-1">Pricing is adjusted to your local labor & material costs</p>
                </div>
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" value={form.city} onChange={(e) => set({ city: e.target.value })} placeholder="Palm Beach Gardens" className="mt-1 h-11 rounded-xl" data-testid="wizard-city-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Is the property occupied?</Label>
                  <RadioGroup value={form.occupied} onValueChange={(v) => set({ occupied: v })} className="mt-2 grid grid-cols-2 gap-2">
                    {[['occupied', 'Occupied'], ['vacant', 'Vacant']].map(([v, t]) => (
                      <label key={v} className={`text-center text-sm rounded-xl border py-2.5 cursor-pointer ${form.occupied === v ? 'border-[#17352D] bg-[#F2F7F5] font-semibold text-[#17352D]' : 'border-[#E6E9EE]'}`}>
                        <RadioGroupItem value={v} className="sr-only" data-testid={`wizard-occupied-${v}`} /> {t}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <div>
                  <Label>Access to the work area</Label>
                  <RadioGroup value={form.access} onValueChange={(v) => set({ access: v })} className="mt-2 grid grid-cols-3 gap-2">
                    {[['easy', 'Easy'], ['limited', 'Limited'], ['difficult', 'Difficult']].map(([v, t]) => (
                      <label key={v} className={`text-center text-xs rounded-xl border py-2.5 cursor-pointer ${form.access === v ? 'border-[#17352D] bg-[#F2F7F5] font-semibold text-[#17352D]' : 'border-[#E6E9EE]'}`}>
                        <RadioGroupItem value={v} className="sr-only" data-testid={`wizard-access-${v}`} /> {t}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="date">Preferred date</Label>
                  <Input id="date" type="date" value={form.preferred_date} onChange={(e) => set({ preferred_date: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="wizard-date-input" />
                </div>
                <div>
                  <Label>Urgency</Label>
                  <RadioGroup value={form.urgency} onValueChange={(v) => set({ urgency: v })} className="mt-2 grid grid-cols-3 gap-2">
                    {['standard', 'urgent', 'emergency'].map(u => (
                      <label key={u} className={`text-center text-xs rounded-xl border py-2 cursor-pointer capitalize ${form.urgency === u ? 'border-[#17352D] bg-[#F2F7F5]' : 'border-[#E6E9EE]'}`}>
                        <RadioGroupItem value={u} className="sr-only" data-testid={`wizard-urgency-${u}`} /> {u}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Contact</div>
              <h1 className="mt-1 font-headline text-3xl md:text-4xl text-[#17352D]">Where should we send your estimate?</h1>
              <p className="text-slate-600 mt-2 text-sm inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#C8922F]" /> No obligation. We respond within 15 minutes during business hours.</p>
            </div>
            <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5 space-y-4">
              <div>
                <Label htmlFor="cn">Full name *</Label>
                <Input id="cn" value={form.customer_name} onChange={(e) => set({ customer_name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="wizard-name-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ce">Email *</Label>
                  <Input id="ce" type="email" value={form.customer_email} onChange={(e) => set({ customer_email: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="wizard-email-input" />
                </div>
                <div>
                  <Label htmlFor="cp">Phone</Label>
                  <Input id="cp" type="tel" value={form.customer_phone} onChange={(e) => set({ customer_phone: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="wizard-phone-input" />
                </div>
              </div>
              <p className="text-xs text-slate-500">By submitting, you agree to be contacted about your project. We never sell your info.</p>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#F2F7F5] border border-[#C7DED4] flex items-center justify-center"><Check className="h-7 w-7 text-[#17352D]" /></div>
              <h1 className="mt-3 font-headline text-3xl md:text-4xl text-[#17352D]">Your estimate</h1>
              <p className="text-slate-600 mt-2">Built from our professional price book, adjusted to your location — preliminary until our free on-site inspection.</p>
            </div>
            {!estimate && loadingAI && (
              <div data-testid="estimate-ballpark-loading" className="rounded-2xl border border-[#EED7A6] bg-[#FBF7EE] p-6">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-[#C8922F]" />
                  <div>
                    <div className="text-sm font-semibold text-[#17352D]">Building your itemized estimate…</div>
                    <div className="text-xs text-slate-500">We're pricing every step of your project against local rates — usually 15–40 seconds. Your request is already saved.</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="h-16 rounded-xl bg-white/70 animate-pulse border border-[#EED7A6]" />
                  <div className="h-16 rounded-xl bg-white/70 animate-pulse border border-[#EED7A6]" />
                </div>
                <div className="mt-3 h-4 w-3/4 rounded bg-white/70 animate-pulse" />
                <div className="mt-2 h-4 w-2/3 rounded bg-white/70 animate-pulse" />
              </div>
            )}
            {!estimate && !loadingAI && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
                Your request has been submitted. Our team will follow up shortly with a detailed quote — AI pricing was unavailable this session.
              </div>
            )}
            {estimate && (
              <div data-testid="estimate-ballpark-pricing-card" className="rounded-2xl border border-[#EED7A6] bg-[#FBF7EE] p-6">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Estimated Project Price</div>
                    <div className="font-headline text-5xl text-[#17352D] mt-1" data-testid="estimate-price">${Number(estimate.price).toLocaleString()}</div>
                    <span className="inline-block mt-2 text-[11px] font-semibold uppercase tracking-wide bg-white border border-[#EED7A6] text-[#C8922F] rounded-full px-2.5 py-1">
                      Preliminary — confirmed after free on-site inspection
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Estimated duration</div>
                    <div className="font-semibold text-[#17352D] text-lg" data-testid="estimate-duration">
                      {estimate.duration_days?.low === estimate.duration_days?.high
                        ? `${estimate.duration_days?.low} day${estimate.duration_days?.low === 1 ? '' : 's'}`
                        : `${estimate.duration_days?.low}–${estimate.duration_days?.high} days`}
                    </div>
                  </div>
                </div>

                {Array.isArray(estimate.scope_categories) && estimate.scope_categories.length > 0 && (
                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Scope of work</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {estimate.scope_categories.map((s, i) => (
                        <span key={i} className="text-xs font-medium bg-white border border-[#EED7A6] text-[#17352D] rounded-full px-3 py-1.5">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Array.isArray(estimate.included) && estimate.included.length > 0 && (
                    <div className="rounded-xl bg-white p-4 border border-[#EED7A6]" data-testid="estimate-included">
                      <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">What's included</div>
                      <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                        {estimate.included.slice(0, 8).map((x, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">✓</span>{x}</li>)}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(estimate.excluded) && estimate.excluded.length > 0 && (
                    <div className="rounded-xl bg-white p-4 border border-[#EED7A6]" data-testid="estimate-excluded">
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Not included</div>
                      <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
                        {estimate.excluded.slice(0, 8).map((x, i) => <li key={i} className="flex gap-2"><span className="text-slate-400">—</span>{x}</li>)}
                      </ul>
                    </div>
                  )}
                </div>

                {Array.isArray(estimate.assumptions) && estimate.assumptions.length > 0 && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-semibold text-[#17352D]">Assumptions we made ({estimate.assumptions.length})</summary>
                    <ul className="mt-2 space-y-1 text-xs text-slate-600">
                      {estimate.assumptions.map((a, i) => <li key={i}>• {a}</li>)}
                    </ul>
                  </details>
                )}
                {estimate.disclaimer && <p className="mt-4 text-[11px] text-slate-500">{estimate.disclaimer}</p>}
              </div>
            )}
            <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
              <div className="font-headline text-xl text-[#17352D]">What&apos;s next?</div>
              <ul className="mt-3 text-sm text-slate-700 space-y-2">
                <li>✅ Your estimate request is saved — our team will call you to schedule a free on-site visit.</li>
                <li>✉️ You&apos;ll receive a written, itemized estimate within 24–48 hours of the site visit.</li>
                <li>🔐 <Link to="/signup" className="text-[#17352D] underline font-semibold">Create an account</Link> to track this estimate, approve work, and view invoices.</li>
              </ul>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Button asChild data-testid="estimate-result-signup-button" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white">
                  <Link to={`/signup?email=${encodeURIComponent(form.customer_email)}&name=${encodeURIComponent(form.customer_name)}`}>Create account to track</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-xl" data-testid="estimate-result-home-button">
                  <Link to="/">Back to home</Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* NAV */}
        {step < 4 && (
          <div className="mt-8 flex items-center justify-between">
            <Button data-testid="estimate-wizard-back-button" variant="outline" disabled={step === 0} onClick={back} className="rounded-xl">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button data-testid={step === 3 ? 'estimate-wizard-submit-button' : 'estimate-wizard-next-button'} disabled={!canNext || submitting || loadingAI} onClick={next} className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white px-6">
              {step === 3 ? (
                submitting || loadingAI ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</> : <><Sparkles className="h-4 w-4 mr-2" /> Get my AI Estimate</>
              ) : (
                <>Continue <ArrowRight className="h-4 w-4 ml-1" /></>
              )}
            </Button>
          </div>
        )}
      </section>
    </Layout>
  );
}
